import { motion } from 'motion/react';
import { 
  FileText, 
  Users, 
  Languages, 
  Clock, 
  Shield, 
  Network,
  Phone,
  Target,
  CreditCard,
  Rocket,
  CheckCircle,
  Lock,
  Heart,
  ChevronDown,
  Info,
  ArrowRight
} from 'lucide-react';
import { useState } from 'react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useTranslation } from '../i18n/useTranslation';

interface LegalSupportPageProps {
  onNavigateHome: () => void;
  onNavigateToHowItWorks: () => void;
  onNavigateToFreeConsultation: () => void;
  onNavigateToPaidSession: () => void;
}

export function LegalSupportPage({ onNavigateHome, onNavigateToHowItWorks, onNavigateToFreeConsultation, onNavigateToPaidSession }: LegalSupportPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { t } = useTranslation();

  const services = [
    {
      icon: FileText,
      title: t('services.legalSupport.whatWeHelp.services.documentation.title'),
      description: t('services.legalSupport.whatWeHelp.services.documentation.description'),
    },
    {
      icon: Users,
      title: t('services.legalSupport.whatWeHelp.services.consultation.title'),
      description: t('services.legalSupport.whatWeHelp.services.consultation.description'),
    },
    {
      icon: Languages,
      title: t('services.legalSupport.whatWeHelp.services.visa.title'),
      description: t('services.legalSupport.whatWeHelp.services.visa.description'),
    },
    {
      icon: Shield,
      title: t('services.legalSupport.whatWeHelp.services.compliance.title'),
      description: t('services.legalSupport.whatWeHelp.services.compliance.description'),
    },
    {
      icon: Clock,
      title: t('services.legalSupport.whatWeHelp.services.representation.title'),
      description: t('services.legalSupport.whatWeHelp.services.representation.description'),
    },
    {
      icon: Network,
      title: t('services.legalSupport.whatWeHelp.services.ongoing.title'),
      description: t('services.legalSupport.whatWeHelp.services.ongoing.description'),
    },
  ];

  const steps = [
    {
      number: 1,
      icon: Phone,
      title: t('services.legalSupport.howItWorks.steps.call.title'),
      description: t('services.legalSupport.howItWorks.steps.call.description'),
    },
    {
      number: 2,
      icon: Target,
      title: t('services.legalSupport.howItWorks.steps.plan.title'),
      description: t('services.legalSupport.howItWorks.steps.plan.description'),
    },
    {
      number: 3,
      icon: CreditCard,
      title: t('services.legalSupport.howItWorks.steps.sessions.title'),
      description: t('services.legalSupport.howItWorks.steps.sessions.description'),
    },
    {
      number: 4,
      icon: Rocket,
      title: t('services.legalSupport.howItWorks.steps.results.title'),
      description: t('services.legalSupport.howItWorks.steps.results.description'),
    },
  ];

  const highlights = [
    {
      icon: CheckCircle,
      title: t('services.legalSupport.whyChoose.highlights.network'),
    },
    {
      icon: Languages,
      title: t('services.legalSupport.whyChoose.highlights.bilingual'),
    },
    {
      icon: Lock,
      title: t('services.legalSupport.whyChoose.highlights.coordination'),
    },
    {
      icon: Heart,
      title: t('services.legalSupport.whyChoose.highlights.peace'),
    },
  ];

  const faqs = [
    {
      question: t('services.legalSupport.faq.questions.legal.question'),
      answer: t('services.legalSupport.faq.questions.legal.answer'),
    },
    {
      question: t('services.legalSupport.faq.questions.fees.question'),
      answer: t('services.legalSupport.faq.questions.fees.answer'),
    },
    {
      question: t('services.legalSupport.faq.questions.selection.question'),
      answer: t('services.legalSupport.faq.questions.selection.answer'),
    },
    {
      question: t('services.legalSupport.faq.questions.support.question'),
      answer: t('services.legalSupport.faq.questions.support.answer'),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Header */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/90 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-white mb-6 text-[40px] font-bold">
                {t('services.legalSupport.hero.title')}
              </h1>
              <p className="text-white/90 mb-8 leading-relaxed text-lg">
                {t('services.legalSupport.hero.description')}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={onNavigateToFreeConsultation}
                className="px-10 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-[#0A1A33] rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {t('services.legalSupport.hero.cta')}
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1659355894099-b2c2b2884322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMGRvY3VtZW50cyUyMHByb2Zlc3Npb25hbHxlbnwxfHx8fDE3NjQ5ODE2NTd8MA&ixlib=rb-4.1.0&q=80&w=1080"
                  alt="Legal documentation and support"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Help With */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[32px]">{t('services.legalSupport.whatWeHelp.title')}</h2>
            <p className="text-[#0A1A33]/60 max-w-2xl mx-auto">
              {t('services.legalSupport.whatWeHelp.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-[#C6A667]/10 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#C6A667]" />
                  </div>
                  <div>
                    <h3 className="text-[#0A1A33] mb-2">{service.title}</h3>
                    <p className="text-[#0A1A33]/60 text-sm">{service.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[32px]">{t('services.legalSupport.howItWorks.title')}</h2>
          </motion.div>

          <div className="hidden lg:block relative mb-12">
            <div className="absolute top-16 left-0 right-0 h-1 bg-[#C6A667]/20 mx-24" />
            
            <div className="grid grid-cols-4 gap-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.15 }}
                    className="relative"
                  >
                    <div className="relative z-10 w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#C6A667] to-[#D4B578] flex items-center justify-center shadow-lg">
                      <Icon className="w-12 h-12 text-white" />
                      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#0A1A33] text-white flex items-center justify-center text-sm">
                        {step.number}
                      </div>
                    </div>
                    
                    <h3 className="text-[#0A1A33] mb-3 text-center font-bold text-sm">
                      {step.title}
                    </h3>
                    <p className="text-[#0A1A33]/70 text-sm leading-relaxed text-center">
                      {step.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="lg:hidden space-y-6 mb-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-[#C6A667]/20"
                >
                  <div className="flex items-start gap-4">
                    <div className="relative flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-[#C6A667] to-[#D4B578] flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                      <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#0A1A33] text-white flex items-center justify-center text-xs">
                        {step.number}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[#0A1A33] mb-2">{step.title}</h3>
                      <p className="text-[#0A1A33]/70 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-[#0A1A33] text-[32px] mb-6">{t('services.legalSupport.pricing.title')}</h2>
            <p className="text-[#0A1A33]/80 max-w-2xl mx-auto mb-4 leading-relaxed">
              {t('services.legalSupport.pricing.description')}
            </p>
            <p className="text-[#0A1A33]/80 max-w-2xl mx-auto mb-4 leading-relaxed">
              {t('services.legalSupport.pricing.details')}
            </p>
            <p className="text-[#0A1A33]/80 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('services.legalSupport.pricing.followUp')}
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={onNavigateToPaidSession}
              className="px-10 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
            >
              <span>{t('services.legalSupport.pricing.cta')}</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[32px]">{t('services.legalSupport.whyChoose.title')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <motion.div
                  key={highlight.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-14 h-14 mb-6 rounded-full bg-[#C6A667]/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] leading-relaxed">{highlight.title}</h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[32px]">{t('services.legalSupport.faq.title')}</h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-[#0A1A33]/10 rounded-2xl overflow-hidden hover:border-[#C6A667]/30 transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="text-[#0A1A33] pr-4 font-bold text-[18px]">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#C6A667] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openFaq === index ? 'auto' : 0,
                    opacity: openFaq === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5 text-[#0A1A33]/70 leading-relaxed">
                    {faq.answer}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A1A33] via-[#0D2347] to-[#0A1A33]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-white mb-8 text-[28px] font-bold">
              {t('services.legalSupport.finalCta.title')}
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={onNavigateToFreeConsultation}
              className="px-10 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-[#0A1A33] rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t('services.legalSupport.finalCta.button')}
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
